/** D-04 — doctor profile + verification status. Name is editable; the rest is read-only. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { getDoctorMe, updateDoctorName } from "../../api/doctor";
import { describeError } from "../../api/errors";
import { ChangePasswordCard } from "../../components/ChangePasswordCard";

export function DoctorProfile() {
  const { data } = useQuery({ queryKey: ["doctor-me"], queryFn: getDoctorMe });
  if (!data) return <p className="text-sm text-gray-400">Loading…</p>;
  return (
    <div className="max-w-lg space-y-8">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">My profile</h2>
        <div className="rounded-xl border bg-white p-5 shadow-xs space-y-4">
          <NameField currentName={data.full_name} />
          <dl className="grid grid-cols-1 gap-3 text-sm border-t pt-4 sm:grid-cols-2">
            <Info label="Status" value={data.status} />
            <Info label="Email" value={data.email ?? "—"} />
            <Info label="Phone" value={data.phone ?? "—"} />
            <Info label="Medical council" value={data.state_medical_council ?? "—"} />
            <Info label="Specialisation" value={data.specialisation ?? "—"} />
            <Info label="License" value={data.license_number_masked || "—"} />
            <Info label="Verified" value={data.verified_at ? new Date(data.verified_at).toLocaleDateString() : "Not verified"} />
          </dl>
        </div>
      </div>

      <ChangePasswordCard />
    </div>
  );
}

function NameField({ currentName }: { currentName: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => updateDoctorName(name.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doctor-me"] });
      setEditing(false);
    },
    onError: (e) => setError(describeError(e)),
  });

  if (!editing) {
    return (
      <div className="flex items-center justify-between">
        <div>
          <dt className="text-xs text-gray-400">Name</dt>
          <dd className="font-medium">{currentName}</dd>
        </div>
        <button
          onClick={() => {
            setName(currentName);
            setError(null);
            setEditing(true);
          }}
          className="rounded-lg border px-3 py-1.5 text-sm"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg border px-3 py-1.5" />
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || name.trim().length === 0}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button onClick={() => setEditing(false)} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-gray-400">{label}</dt>
      {/* A long email or licence number wraps in its column instead of widening the page (RESP-22). */}
      <dd className="font-medium wrap-anywhere">{value}</dd>
    </div>
  );
}
